import * as queryString from 'query-string';

/* We use an 'any' type to signify that we will be accepting any generic type passed as a request or response */
// tslint:disable-next-line
type TopicMap = { [topic: string]: (parameter: any) => any };
type WindowWithTopicMap = { microViewTopicMap: TopicMap };
type CommunicationServiceResponse<ResponseType> = { data?: ResponseType; error?: string };

class MicroviewCommunicationService {
  public static instance: MicroviewCommunicationService = new MicroviewCommunicationService();
  private topicMap: TopicMap = {};

  protected constructor() {
    const windowWithTopicMap = (window as unknown) as WindowWithTopicMap;
    if (!windowWithTopicMap.microViewTopicMap) {
      windowWithTopicMap.microViewTopicMap = this.topicMap;
    } else {
      this.topicMap = windowWithTopicMap.microViewTopicMap;
    }
  }

  public send<Request, Response>(topic: string, data: Request): CommunicationServiceResponse<Response> {
    const result: CommunicationServiceResponse<Response> = {};
    try {
      result.data = this.topicMap[topic](data);
    } catch (e) {
      result.error = 'Communication service error: ' + e + '\nTopic: ' + topic;
      const microViewDebugValue = (queryString.parse(location.search) as { microViewDebug: string }).microViewDebug;
      if (microViewDebugValue && microViewDebugValue !== 'none') {
        // tslint:disable-next-line:no-console
        console.error(result.error);
      }
    }
    return result;
  }

  public listen<Request, Response>(topic: string, handler: (data: Request) => Response): () => void {
    if (this.topicMap[topic]) {
      throw new Error('ERROR: Already listening to ' + topic);
    }

    this.topicMap[topic] = handler;
    return () => {
      delete this.topicMap[topic];
    };
  }
}

export abstract class MicroviewCommunicationChannel<Request, Response> {
  public abstract name: string;
  public abstract namespace: string;

  private getFullName(): string {
    return this.namespace + '.' + this.name;
  }

  // This function returns the function to unsubscribe. Please remember to unsubscribe.
  public listen(handler: (r: Request) => Response): () => void {
    return MicroviewCommunicationService.instance.listen<Request, Response>(this.getFullName(), handler);
  }

  public call(r: Request): CommunicationServiceResponse<Response> {
    return MicroviewCommunicationService.instance.send<Request, Response>(this.getFullName(), r);
  }
}
